/* eslint-disable react-hooks/rules-of-hooks */
import 'app/components/PACSConfiguration/style.scss';

import React, { useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider, Form, Icon } from 'semantic-ui-react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';

import ControlledLabeledInput from 'app/ReactUseForm/ControlledLabeledInput';
import PACSAssociationStatus from 'app/components/PACSAssociationStatus';
import PACSCommunicationInjector from 'app/injectors/PACSCommunication/context';
import { AssociationParameters, PACSError } from 'app/interfaces/PACSCommunication';
import PACSErrorMessage from 'app/components/PACSErrorMessage';
import MinDurationPromise from 'app/utils/MinDurationPromise';
import ActionResultButton from 'app/components/ActionResultButton';
import useMinDurationLoadingState from 'app/hooks/useMinDurationLoadingState';
import queryPACSAssociationStatusOptions, {
  addAllPresentationContextsToConfiguration,
} from 'app/queries/queryPACSAssociationStatusOptions';
import {
  type PACSConfigurationState,
  selectPACSConfiguration,
  updatePACSAssociation,
} from 'app/redux/PACSConfiguration/reducer';
import _ from 'lodash';

const MINIMUM_ASSOCIATION_DURATION = 1000;

export default function PACSAssociationConfiguration() {
  const PACSCommunicationCtor = useContext(PACSCommunicationInjector);
  if (!PACSCommunicationCtor) return null;

  const [pacsCommunication] = useState(() => new PACSCommunicationCtor());
  const pacsConfiguration = useSelector(selectPACSConfiguration);
  const dispatch = useDispatch();

  const onAssociationSuccess = (associationParameters: PACSConfigurationState) => {
    console.debug('associationParameters', associationParameters);
    dispatch(updatePACSAssociation({ ...associationParameters, enabled: true }));
  };

  const {
    handleSubmit,
    formState: { isValid },
    control,
    watch,
  } = useForm<PACSConfigurationState>({
    defaultValues: {
      hostIP: pacsConfiguration?.hostIP ?? '',
      port: pacsConfiguration?.port ?? 4242,
      hostAETitle: pacsConfiguration?.hostAETitle ?? '',
      selfAETitle: pacsConfiguration?.selfAETitle ?? 'PICOXIA',
      username: pacsConfiguration?.username ?? '',
      password: pacsConfiguration?.password ?? '',
      timeout: pacsConfiguration?.timeout ?? 60,
      enabled: pacsConfiguration?.enabled,
    },
    resetOptions: { keepDirtyValues: true },
    mode: 'onBlur',
  });

  const rules = {
    hostIP: { required: true },
    port: { required: true, min: 0, max: 65535 },
    hostAETitle: { required: true, maxLength: 16 },
    selfAETitle: { maxLength: 16 },
    timeout: { min: 0 },
  } as const;

  const configuration = watch();
  const isNewConfiguration = useMemo(() => {
    return !_.isEqual(_.pick(pacsConfiguration, Object.keys(configuration)), configuration);
  }, [...Object.values(configuration), pacsConfiguration]);

  const { refetch, error, ...rest } = useQuery({
    ...queryPACSAssociationStatusOptions(addAllPresentationContextsToConfiguration(configuration), {
      pacsCommunication,
    }),
    enabled: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
  const isFetching = useMinDurationLoadingState(rest.isFetching, MINIMUM_ASSOCIATION_DURATION);

  return (
    <Form className="pacs-configuration" onSubmit={handleSubmit(onAssociationSuccess)}>
      <div className="pacs-configuration__fields">
        <div>
          <ControlledLabeledInput
            control={control}
            name="hostIP"
            rules={rules.hostIP}
            label={<FormattedMessage id="pacs.configuration.host_ip" />}
            fluid
            id="pacs.configuration.host_ip"
          />
          <ControlledLabeledInput
            control={control}
            name="port"
            rules={rules.port}
            label={<FormattedMessage id="pacs.configuration.port" />}
            fluid
            id="pacs.configuration.port"
            type="number"
          />
          <ControlledLabeledInput
            control={control}
            name="hostAETitle"
            rules={rules.hostAETitle}
            label={<FormattedMessage id="pacs.configuration.host_ae_title" />}
            fluid
            id="pacs.configuration.host_ae_title"
            type="text"
          />
          <ControlledLabeledInput
            control={control}
            name="selfAETitle"
            rules={rules.selfAETitle}
            label={<FormattedMessage id="pacs.configuration.self_ae_title" />}
            fluid
            id="pacs.configuration.self_ae_title"
            type="text"
          />
        </div>
        <div>
          <ControlledLabeledInput
            control={control}
            name="username"
            label={<FormattedMessage id="pacs.configuration.username" />}
            fluid
            id="pacs.configuration.username"
            type="text"
          />
          <ControlledLabeledInput
            control={control}
            name="password"
            label={<FormattedMessage id="pacs.configuration.password" />}
            fluid
            id="pacs.configuration.password"
            type="password"
          />
          <ControlledLabeledInput
            control={control}
            name="timeout"
            rules={rules.timeout}
            label={<FormattedMessage id="pacs.configuration.timeout" />}
            fluid
            id="pacs.configuration.timeout"
            type="number"
          />
        </div>
      </div>
      <Divider clearing />
      {error && (
        <PACSErrorMessage
          error={error as unknown as PACSError}
          titleID="pacs.configuration.association_error.title"
        />
      )}

      <div className="pacs-configuration__buttons">
        {isNewConfiguration && (
          <PACSAssociationStatus
            pacsConfiguration={configuration}
            pacsCommunication={pacsCommunication}
            isDisableActiveCheck
          />
        )}
        <Button
          primary
          onClick={handleSubmit(onAssociationSuccess)}
          type="submit"
          disabled={!isValid || isFetching}
        >
          <FormattedMessage id="general.save" />
        </Button>

        <ActionResultButton
          secondary
          onClick={() =>
            new Promise<AssociationParameters>((res, rej) =>
              handleSubmit(
                (associationParameters) =>
                  MinDurationPromise(refetch(), MINIMUM_ASSOCIATION_DURATION)
                    .then(({ isError }) => {
                      if (isError) rej(error);
                      res(associationParameters);
                    })
                    .catch((...args) => rej(...args)),
                rej
              )()
            )
          }
          loading={isFetching}
          disabled={!isValid || isFetching}
          size="tiny"
          type="button"
          failureProps={{ secondary: false, color: 'red' }}
          successProps={{ secondary: false, color: 'green' }}
          onSuccess={(associationParameters) => onAssociationSuccess(associationParameters)}
          successContent={
            <>
              <Icon name="check" />
              <FormattedMessage id="pacs.association_status.connected" />
            </>
          }
          failureContent={
            <>
              <Icon name="close" />
              <FormattedMessage id="pacs.configuration.testAndSave" />
            </>
          }
          delay={1000}
        >
          {isFetching ? (
            <>
              <Icon name="sync" loading />
              <FormattedMessage id="pacs.configuration.connecting" />
            </>
          ) : (
            <FormattedMessage id="pacs.configuration.testAndSave" />
          )}
        </ActionResultButton>
      </div>
    </Form>
  );
}
